import {
  CSV_COMMENT_PREFIX,
  CSV_LIST_SEPARATOR,
  type CsvField,
  type CsvFieldType,
  type CsvSchema,
  splitList
} from './schema';

const MIN_EXAMPLE_ROWS = 2;

/** A comment row itself, so it is skipped on upload too. */
const TEMPLATE_NOTE = `${CSV_COMMENT_PREFIX} Rows starting with ${CSV_COMMENT_PREFIX} are examples and are skipped on upload. Replace them with your own rows.`;

/** Enough example rows for every enum value to appear, since the template is the only docs most people read. */
const exampleRowCount = (schema: CsvSchema): number =>
  Math.max(
    MIN_EXAMPLE_ROWS,
    ...schema.fields.map((field) => (field.type === 'enum' ? (field.enumValues?.length ?? 0) : 0))
  );

/** Obviously fake defaults, two per type so example rows differ. Enums use their allowed values. */
const TYPE_EXAMPLES: Record<CsvFieldType, string[]> = {
  string: ['Example', 'Sample'],
  number: ['1', '2'],
  boolean: ['true', 'false'],
  date: ['2026-01-31', '2026-02-28'],
  enum: [],
  phone: ['+15035550100', '+15035550101'],
  email: ['first@example.com', 'second@example.com']
};

/** RFC 4180 quoting, as Excel expects. */
const escapeCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** One column's value in example row `index`; enum columns step through their allowed values. */
const exampleValue = (field: CsvField, index: number): string => {
  const values = field.enumValues ?? [];
  if (field.type === 'enum' && values.length > 0) {
    const value = values[index % values.length] ?? '';
    if (!field.multiple) {
      return value;
    }
    const next = values[(index + 1) % values.length];
    return next && next !== value ? [value, next].join(CSV_LIST_SEPARATOR) : value;
  }
  const fallback = TYPE_EXAMPLES[field.type];
  const value = field.example ?? fallback[index % fallback.length] ?? '';
  if (!field.multiple || splitList(value).length > 1) {
    return value;
  }
  const second = fallback.find((candidate) => candidate !== value) ?? `${value} 2`;
  return [value, second].join(CSV_LIST_SEPARATOR);
};

/** A header row plus commented example rows, so the file can be uploaded as-is without importing them. */
export const generateTemplate = (schema: CsvSchema): string => {
  const header = schema.fields.map((field) => escapeCell(field.label));
  const examples = Array.from({ length: exampleRowCount(schema) }, (_, index) =>
    schema.fields
      .map((field, column) => {
        const value = exampleValue(field, index);
        return escapeCell(column === 0 ? `${CSV_COMMENT_PREFIX} ${value}` : value);
      })
      .join(',')
  );
  return [header.join(','), escapeCell(TEMPLATE_NOTE), ...examples].join('\n') + '\n';
};

const slug = (listName: string): string =>
  listName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const templateFilename = (listName: string): string => `${slug(listName)}-template.csv`;

/** Dated so successive exports are distinguishable. */
export const exportFilename = (listName: string, on = new Date()): string =>
  `${slug(listName)}-${on.toISOString().slice(0, 10)}.csv`;
